import type { Asset } from 'contentful'
import type {
  TypeResponsiveImageWithoutUnresolvableLinksResponse,
  TypeCallToActionWithoutUnresolvableLinksResponse,
  TypeCardWithoutUnresolvableLinksResponse,
  TypeBackdropWithoutUnresolvableLinksResponse,
  TypeLinkablesWithoutUnresolvableLinksResponse,
  TypeNavigationGroupWithoutUnresolvableLinksResponse,
  TypeEventWithoutUnresolvableLinksResponse,
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeAsideCardWithoutUnresolvableLinksResponse,
  TypeHeroBlockWithoutUnresolvableLinksResponse,
  TypeVisibilitySettingsWithoutUnresolvableLinksResponse,
  TypeMediaWithoutUnresolvableLinksResponse,
  TypeMilestoneWithoutUnresolvableLinksResponse,
  TypeDownloadableGroupWithoutUnresolvableLinksResponse,
  TypeTagWithoutUnresolvableLinksResponse,
  TypeProductDetailsPageWithoutUnresolvableLinksResponse,
  TypeDownloadableDocumentWithoutUnresolvableLinksResponse,
  TypeMarketingMaterialSetWithoutUnresolvableLinksResponse,
  TypeProductWithoutUnresolvableLinksResponse,
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
  TypePageBuilderWithoutUnresolvableLinksResponse,
  TypeContentBlockWithoutUnresolvableLinksResponse,
  TypePodcastWithoutUnresolvableLinksResponse,
  TypeVideoContentWithoutUnresolvableLinksResponse,
  TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse,
  TypeHotspotWithoutUnresolvableLinksResponse,
  TypePartnerWithoutUnresolvableLinksResponse,
  TypeArticleSetWithoutUnresolvableLinksResponse,
  TypeLocalizationStringWithoutUnresolvableLinksResponse,
  TypeGatedContentWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function instanceOfAsset(object: any): object is Asset {
  return object?.sys?.type === 'Asset'
}

// Shared global function that checks the 3 things to assert an entry is of a type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function instanceOfSpecificEntry(contentTypeId: string, object: any) {
  // Check if not 'undefined', check that object has 'fields' (Unresolved Link), check if correct ID
  return (
    !!object &&
    typeof object === 'object' &&
    'fields' in object &&
    object?.sys?.contentType?.sys?.id === contentTypeId
  )
}

export function instanceOfArticle(
  object: unknown,
): object is TypeArticleWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('article', object)
}

export function instanceOfArticleSet(
  object: unknown,
): object is TypeArticleSetWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('articleSet', object)
}

export function instanceOfAsideCard(
  object: unknown,
): object is TypeAsideCardWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('asideCard', object)
}

export function instanceOfBackdrop(
  object: unknown,
): object is TypeBackdropWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('backdrop', object)
}

export function instanceOfCallToAction(
  object: unknown,
): object is TypeCallToActionWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('callToAction', object)
}

export function instanceOfCard(
  object: unknown,
): object is TypeCardWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('card', object)
}

export function instanceOfContentBlock(
  object: unknown,
): object is TypeContentBlockWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('contentBlock', object)
}

export function instanceOfDownloadableAsset(
  object: unknown,
): object is TypeDownloadableAssetWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('downloadableAsset', object)
}

export function instanceOfDownloadableDocument(
  object: unknown,
): object is TypeDownloadableDocumentWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('downloadableDocument', object)
}

export function instanceOfDownloadableGroup(
  object: unknown,
): object is TypeDownloadableGroupWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('downloadableGroup', object)
}

export function instanceOfEvent(
  object: unknown,
): object is TypeEventWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('event', object)
}

export function instanceOfGatedContent(
  object: unknown,
): object is TypeGatedContentWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('gatedContent', object)
}

export function instanceOfHeroBlock(
  object: unknown,
): object is TypeHeroBlockWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('heroBlock', object)
}

export function instanceOfLinkable(
  object: unknown,
): object is TypeLinkablesWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('linkables', object)
}

export function instanceOfMarketingMaterial(
  object: unknown,
): object is TypeLinkablesWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('marketingMaterial', object)
}

export function instanceOfMarketingMaterialSet(
  object: unknown,
): object is TypeMarketingMaterialSetWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('marketingMaterialSet', object)
}

export function instanceOfMedia(
  object: unknown,
): object is TypeMediaWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('media', object)
}

export function instanceOfMilestone(
  object: unknown,
): object is TypeMilestoneWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('milestone', object)
}

// Navigation can be either a navigation group or a linkable. Check for either
export function instanceOfNavigation(
  object: unknown,
): object is
  | TypeNavigationGroupWithoutUnresolvableLinksResponse
  | TypeLinkablesWithoutUnresolvableLinksResponse {
  return instanceOfNavigationGroup(object) || instanceOfLinkable(object)
}

export function instanceOfNavigationGroup(
  object: unknown,
): object is TypeNavigationGroupWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('navigationGroup', object)
}

export function instanceOfPageBuilder(
  object: unknown,
): object is TypePageBuilderWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('pageBuilder', object)
}

export function instanceOfPodcast(
  object: unknown,
): object is TypePodcastWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('podcast', object)
}

export function instanceOfProduct(
  object: unknown,
): object is TypeProductWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('product', object)
}

export function instanceOfProductDetailsPage(
  object: unknown,
): object is TypeProductDetailsPageWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('productDetailsPage', object)
}

export function instanceOfResponsiveImage(
  object: unknown,
): object is TypeResponsiveImageWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('responsiveImage', object)
}

export function instanceOfTag(
  object: unknown,
): object is TypeTagWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('tag', object)
}

export function instanceOfVideoContent(
  object: unknown,
): object is TypeVideoContentWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('videoContent', object)
}

export function instanceOfSpecialFeaturesSlide(
  object: unknown,
): object is TypeSpecialFeaturesSlideWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('specialFeaturesSlide', object)
}

export function instanceOfVisiblity(
  object: unknown,
): object is TypeVisibilitySettingsWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('visibilitySettings', object)
}

export function instanceOfHotspot(
  object: unknown,
): object is TypeHotspotWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('hotspot', object)
}

export function instanceOfPartner(
  object: unknown,
): object is TypePartnerWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('partner', object)
}

export function instanceOfLocalizationString(
  object: unknown,
): object is TypeLocalizationStringWithoutUnresolvableLinksResponse {
  return instanceOfSpecificEntry('localizationString', object)
}
